/**
 * Portal v2 senders list (`/portal/senders`).
 *
 * View-only — no edit affordances (differs from partner-side senders).
 * SectionHeader + CardV2 div-grid table: source IP (text avatar from first
 * digit + mono-data IP), org, country, total/passed/failed (tnum), pass-
 * rate StatPill.
 *
 * Backend exposes IP-level senders only — no "category" classification,
 * so we use pass-rate StatPill for the only tone signal.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Server, Users } from "lucide-react";
import { Skeleton } from "@ddmarc/ui";
import { ExportMenu } from "@/components/v2/export-menu";
import { portalApi, type PortalSender } from "@/lib/portal-api";
import { usePortalScope } from "@/lib/portal-scope-context";
import {
  CardV2,
  SectionHeader,
  StatPill,
} from "@/components/primitives";

const passRateTone = (pct: number): "good" | "warn" | "danger" =>
  pct >= 95 ? "good" : pct >= 80 ? "warn" : "danger";

function ipInitial(ip: string): string {
  return ip.split(".")[0]?.slice(0, 2) || "·";
}

export function PortalSendersV2Page() {
  const { selectedDomainId } = usePortalScope();
  const { data, isLoading } = useQuery({
    queryKey: ["portal", "senders", selectedDomainId],
    queryFn: () =>
      portalApi.listSenders({ size: 50, domain_id: selectedDomainId }),
  });

  const items: PortalSender[] = data?.items ?? [];

  return (
    <div className="space-y-3.5">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Senders
          </span>
        }
        subtitle="Email sources and their authentication results."
        actions={<ExportMenu kinds={["senders", "summary"]} />}
      />

      <CardV2 bodyClassName="p-0">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Server className="h-10 w-10 text-[var(--v2-fg-faint)]" />
            <p className="text-[13px] font-medium text-foreground">
              No senders yet
            </p>
            <p className="text-[12.5px] text-muted-foreground">
              Senders will appear here once reports are processed.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            <div className="grid grid-cols-[1.4fr_1.3fr_0.7fr_0.7fr_0.7fr_0.7fr_0.7fr] gap-3 px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
              <span>Source IP</span>
              <span>Organization</span>
              <span>Country</span>
              <span className="text-right">Total</span>
              <span className="text-right">Passed</span>
              <span className="text-right">Failed</span>
              <span className="text-right">Pass</span>
            </div>
            {items.map((s, idx) => (
              <div
                key={`${s.source_ip}-${idx}`}
                className="grid grid-cols-[1.4fr_1.3fr_0.7fr_0.7fr_0.7fr_0.7fr_0.7fr] items-center gap-3 px-4 py-[var(--v2-row-py,0.625rem)] text-[12.5px]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-[10px] font-semibold uppercase text-muted-foreground"
                  >
                    {ipInitial(s.source_ip)}
                  </span>
                  <Link
                    to="/portal/senders/$sourceIp"
                    params={{ sourceIp: s.source_ip }}
                    className="truncate font-mono-data text-foreground underline-offset-2 hover:underline"
                  >
                    {s.source_ip}
                  </Link>
                </span>
                <span className="truncate text-muted-foreground">
                  {s.asn_org || "—"}
                </span>
                <span className="text-foreground">{s.country || "—"}</span>
                <span className="tnum text-right text-foreground">
                  {s.total_emails.toLocaleString()}
                </span>
                <span className="tnum text-right text-[var(--v2-good)]">
                  {s.passed_emails.toLocaleString()}
                </span>
                <span className="tnum text-right text-[var(--v2-danger)]">
                  {s.failed_emails.toLocaleString()}
                </span>
                <span className="text-right">
                  <StatPill tone={passRateTone(s.pass_rate)}>
                    {s.pass_rate.toFixed(1)}%
                  </StatPill>
                </span>
              </div>
            ))}
          </div>
        )}
      </CardV2>
    </div>
  );
}
